import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Subject, takeUntil, catchError, of } from 'rxjs';
import { formatDate } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  KillSwitchLogEntry,
  KillSwitchService,
  KillSwitchType
} from '../services/kill-switch.service';

@Component({
  selector: 'app-kill-switch-logs',
  standalone: false,
  templateUrl: './kill-switch-logs.component.html',
  styleUrl: './kill-switch-logs.component.css'
})
export class KillSwitchLogsComponent implements OnInit, OnDestroy {

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  rows: KillSwitchLogEntry[] = [];
  totalCount = 0;
  pageIndex = 0;
  pageSize = 10;
  isLoading = false;

  fromDate: Date | null = null;
  toDate: Date | null = null;
  switchType: KillSwitchType | '' = '';

  expandedIndex: number | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly killSwitchService: KillSwitchService,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadLogs();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.expandedIndex = null;
    this.loadLogs();
  }

  onDateRangeChange(): void {
    if ((this.fromDate && this.toDate) || (!this.fromDate && !this.toDate)) {
      this.applyFilters();
    }
  }

  applyFilters(): void {
    this.pageIndex = 0;
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.expandedIndex = null;
    this.loadLogs();
  }

  toggleExpand(index: number): void {
    this.expandedIndex = this.expandedIndex === index ? null : index;
  }

  clearDateFilter(): void {
    if (!this.fromDate && !this.toDate) {
      return;
    }
    this.fromDate = null;
    this.toDate = null;
    this.applyFilters();
  }

  get hasDateFilter(): boolean {
    return !!(this.fromDate || this.toDate);
  }

  switchTypeLabel(type: KillSwitchType | string): string {
    return type === 'Pool' ? 'Pool Switch' : 'Master';
  }

  formatTimestamp(value: string): string {
    if (!value) return '';
    try {
      return formatDate(value, 'MM-dd-y | h:mm a', 'en-US');
    } catch {
      return value;
    }
  }

  stateLabel(enabled: boolean): string {
    return enabled ? 'On' : 'Off';
  }

  private loadLogs(): void {
    this.isLoading = true;
    this.killSwitchService.getLogs({
      page: this.pageIndex + 1,
      pageSize: this.pageSize,
      fromDate: this.fromDate ? formatDate(this.fromDate, 'yyyy-MM-dd', 'en-US') : null,
      toDate: this.toDate ? formatDate(this.toDate, 'yyyy-MM-dd', 'en-US') : null,
      switchType: this.switchType || null
    }).pipe(
      catchError(err => {
        console.error('[KillSwitchLogs] failed to load:', err);
        this.snackBar.open('Could not load audit trail.', 'Close', { duration: 4000 });
        return of({ page: this.pageIndex + 1, pageSize: this.pageSize, totalCount: 0, data: [] });
      }),
      takeUntil(this.destroy$)
    ).subscribe(res => {
      this.rows = res.data;
      this.totalCount = res.totalCount;
      this.isLoading = false;
    });
  }
}
